import React, { memo, useMemo } from 'react';
import { useI18n } from '../../i18n';
import { useExchangeRates } from '../../context/ExchangeRatesContext';
import { formatNumber } from '../../lib/utils';

const TICKER_CURRENCIES = ['EUR', 'AUD', 'CAD'];
const SEGMENT_GAP = '   ·   ';

function formatAsOfDate(isoDate, language) {
  if (!isoDate) return '';
  const locale = language === 'es' ? 'es-CO' : 'en-US';
  try {
    return new Date(`${isoDate}T12:00:00`).toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

function buildTickerText({ trmCopPerUsd, rates, ratesAsOf, ratesStale, t, language }) {
  const parts = [];
  const toUsd = rates?.to_usd;

  if (trmCopPerUsd != null && Number.isFinite(Number(trmCopPerUsd))) {
    parts.push(
      t('exchangeRates.tickerTrm', {
        value: formatNumber(trmCopPerUsd, { maximumFractionDigits: 2 }),
      }),
    );
  }

  for (const currency of TICKER_CURRENCIES) {
    const usdPerUnit = toUsd?.[currency];
    if (usdPerUnit == null || !Number.isFinite(Number(usdPerUnit))) continue;
    parts.push(
      t('exchangeRates.tickerForex', {
        currency,
        value: formatNumber(usdPerUnit, {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        }),
      }),
    );
  }

  const asOfLabel = formatAsOfDate(ratesAsOf, language);
  if (asOfLabel) {
    parts.push(t('exchangeRates.tickerAsOf', { date: asOfLabel }));
  }

  if (ratesStale) {
    parts.push(t('exchangeRates.tickerStale'));
  }

  return parts.join(SEGMENT_GAP);
}

function TickerSegment({ text, hidden }) {
  return (
    <div
      className="flex shrink-0 items-center py-1.5 px-6"
      aria-hidden={hidden || undefined}
    >
      <span className="whitespace-nowrap">{text}</span>
    </div>
  );
}

export default memo(function ExchangeRatesTicker() {
  const { t, language } = useI18n();
  const { rates, loading, trmCopPerUsd, ratesAsOf, ratesStale } = useExchangeRates();

  const text = useMemo(
    () =>
      buildTickerText({
        trmCopPerUsd,
        rates,
        ratesAsOf,
        ratesStale,
        t,
        language,
      }),
    [trmCopPerUsd, rates, ratesAsOf, ratesStale, t, language],
  );

  if (!text) return null;
  if (loading && !rates) return null;

  return (
    <div
      className="bg-slate-800 text-slate-100 text-xs border-b border-slate-700"
      data-testid="exchange-rates-ticker"
      role="region"
      aria-label={t('exchangeRates.tickerAria')}
    >
      <div className="overflow-hidden motion-reduce:hidden">
        <div className="flex w-max animate-exchange-marquee hover:[animation-play-state:paused]">
          <TickerSegment text={text} />
          <TickerSegment text={text} hidden />
        </div>
      </div>
      <p className="hidden motion-reduce:block px-4 py-2 text-center text-[11px] leading-snug">
        {text}
      </p>
    </div>
  );
});
